import { useEffect, useState } from "react";
import type { Usuario } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Cargando } from "../../components/Cargando";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Select } from "../../components/Formulario";
import { useEstadoEnUrl } from "../../hooks/useEstadoEnUrl";
import { api } from "../../lib/apiClient";
import { ColaTrabajoVista } from "../mi-trabajo/ColaTrabajoVista";
import {
  construirColaTrabajo,
  type ActuacionApi,
  type ActuacionPendienteApi,
  type CaducidadApi,
  type EtapaProcesoApi,
  type SinMovimientoApi,
} from "../mi-trabajo/colaTrabajo";

// Forma exacta de GET /centro-operaciones (ver obtenerCentroOperaciones() en
// functions/_app/lib/centro-operaciones.ts). `audiencias` es la única
// categoría de esa función que "Mi Trabajo" no reenvía (la cubre con su
// propio actuacionesPeriodo del período elegido — ver routes/mi-trabajo.ts),
// así que acá sí hace falta leerla directo y adaptarla más abajo.
interface DatosCentroOperaciones {
  audiencias: ActuacionPendienteApi[];
  caducidades: CaducidadApi[];
  escritosPendientes: ActuacionPendienteApi[];
  clientesEsperandoRespuesta: ActuacionPendienteApi[];
  expedientesSinMovimiento: SinMovimientoApi[];
  etapasSinResponsable: EtapaProcesoApi[];
  etapasVencidas: EtapaProcesoApi[];
  etapasPorVencer: EtapaProcesoApi[];
  periciasPendientes: ActuacionPendienteApi[];
  oficiosSinRespuesta: ActuacionPendienteApi[];
  notificacionesPendientes: ActuacionPendienteApi[];
  // Se sigue recibiendo (el cálculo y el dato no se tocan): sólo se dejó de
  // mostrar en un panel propio, porque repetía exactamente los mismos 5
  // números que ya aparecen, con más detalle, en la cola de abajo.
  alertasCriticas: { tipo: string; etiqueta: string; cantidad: number; severidad: "info" | "advertencia" | "error" }[];
}

// Centro de Operaciones = el mismo agrupador ya construido y probado en "Mi
// Trabajo" (construirColaTrabajo/agruparPorExpediente + ColaTrabajoVista,
// ver ../mi-trabajo/colaTrabajo.ts), sin el filtro "sólo yo": mismo backend
// (GET /centro-operaciones ya es obtenerCentroOperaciones(), con un
// responsable_id opcional agregado — ver routes/centro-operaciones.ts),
// mismo componente de render, mostrando además Responsable, Unidad de
// Negocio y antigüedad real de cada vencido por fila
// (mostrarResponsable/mostrarUnidadNegocio/mostrarAntiguedad, opt-in — Mi
// Trabajo no los pasa y queda exactamente igual que antes).
//
// No hay "período" en esta pantalla — agenda/actuaciones propias no
// corresponden a "todo el estudio" — por eso atrasados/eventosPeriodo/
// honorariosPendientesLista van vacíos: construirColaTrabajo() ya soporta
// ese caso sin ningún cambio (Mi Trabajo lo ejercita igual cuando el
// período elegido no tiene nada en esas categorías).
export function CentroOperacionesPage() {
  const { permisos } = useAuth();
  const [datos, setDatos] = useState<DatosCentroOperaciones | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [responsableId, setResponsableId] = useEstadoEnUrl("responsable", "");
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);

  function cargar() {
    const parametros = responsableId ? `?responsable_id=${encodeURIComponent(responsableId)}` : "";
    api
      .get<DatosCentroOperaciones>(`/centro-operaciones${parametros}`)
      .then(setDatos)
      .catch(() => setError("No se pudo cargar el Centro de Operaciones."));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [responsableId]);

  // Sin gating por expedientes.ver_todos (a diferencia de Mi Trabajo): esta
  // pantalla ya muestra el trabajo de TODO el estudio sin el filtro, así
  // que ofrecer "acotar a un responsable" nunca amplía lo que la sesión ya
  // podía ver.
  useEffect(() => {
    api
      .get<{ items: Usuario[] }>("/usuarios")
      .then((d) => setUsuarios(d.items.filter((u) => u.activo)))
      .catch(() => undefined);
  }, []);

  if (error) return <EstadoVacio titulo="Error" descripcion={error} />;
  if (!datos) return <Cargando />;

  const hoyIso = new Date().toISOString().substring(0, 10);
  // audiencias ya son sólo de tipo "Audiencia" por construcción de esa
  // consulta (ver audienciasP en centro-operaciones.ts) — se adapta acá a
  // ActuacionApi únicamente para reusar categoriaDeActuacion() sin
  // duplicarla, no es un dato nuevo.
  const actuacionesPeriodo: ActuacionApi[] = datos.audiencias.map((a) => ({ ...a, tipo: "Audiencia" }));

  const grupos = construirColaTrabajo(
    {
      atrasados: [],
      eventosPeriodo: [],
      actuacionesPeriodo,
      honorariosPendientesLista: [],
      caducidades: datos.caducidades,
      escritosPendientes: datos.escritosPendientes,
      clientesEsperandoRespuesta: datos.clientesEsperandoRespuesta,
      periciasPendientes: datos.periciasPendientes,
      oficiosSinRespuesta: datos.oficiosSinRespuesta,
      notificacionesPendientes: datos.notificacionesPendientes,
      expedientesSinMovimiento: datos.expedientesSinMovimiento,
      etapasSinResponsable: datos.etapasSinResponsable,
      etapasVencidas: datos.etapasVencidas,
      etapasPorVencer: datos.etapasPorVencer,
    },
    hoyIso,
  );

  return (
    <div>
      <EncabezadoPagina
        titulo="Centro de Operaciones"
        descripcion="Qué requiere seguimiento y dónde intervenir — agrupado por expediente, igual que Mi Trabajo."
      />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-texto-secundario">
          <span className="text-2xl font-semibold text-texto">{grupos.length}</span>{" "}
          {grupos.length === 1 ? "expediente requiere seguimiento" : "expedientes requieren seguimiento"}
        </p>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-texto">Responsable</span>
          <Select value={responsableId} onChange={(e) => setResponsableId(e.target.value)} className="min-w-[12rem]">
            <option value="">Todos</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre_completo}
              </option>
            ))}
          </Select>
        </label>
      </div>

      <ColaTrabajoVista
        grupos={grupos}
        hoyIso={hoyIso}
        mostrarResponsable
        mostrarUnidadNegocio
        mostrarAntiguedad
        puedeEditarAgenda={permisos.has("agenda.editar")}
        onAccionCompletada={cargar}
      />
    </div>
  );
}
